import { Form, Input, Radio, Button, Space, Card, message, theme } from 'antd';
import {
  VersionAddParams,
  addVersion,
  updateVersion,
  VersionItem,
} from '@/api/version';
import { translate } from '@/api/translate';
import { useState, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { LoadingOutlined, TranslationOutlined } from '@ant-design/icons';
import { useFormDrawer } from '@/hooks/useFormDrawer';
import styles from './AddVersionDrawer.module.less';

interface VersionDrawerProps {
  visible: boolean;
  editData?: VersionItem;
  onSuccess: () => void;
  onClose: () => void;
  systemPlatform?: 1;
}

interface TranslateButtonProps {
  onTranslate: () => void;
  loading?: boolean;
}

const TranslateButton = ({ onTranslate, loading }: TranslateButtonProps) => (
  <Button
    type="primary"
    size="small"
    ghost
    className={styles.translateButton}
    onClick={onTranslate}
    loading={loading}
  >
    翻译
  </Button>
);

const { TextArea } = Input;
const { useToken } = theme;

const VersionDrawer: React.FC<VersionDrawerProps> = ({
  visible,
  editData,
  onSuccess,
  onClose,
  systemPlatform
}) => {
  const [translating, setTranslating] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(!editData);
  const [messageApi, contextHolder] = message.useMessage();
  const { token } = useToken();

  const { DrawerForm, form } = useFormDrawer<VersionAddParams>({
    title: editData ? '编辑版本' : '新增版本',
    width: '85%',
    onClose,
    async onSubmit(values) {
      try {
        const resp = editData
          ? await updateVersion({ ...values, id: editData.id, systemPlatform })
          : await addVersion({ ...values, systemPlatform });
        if (resp.status === 200) {
          onSuccess();
          return true;
        }
        messageApi.error(
          resp.message || (editData ? '编辑版本失败' : '添加版本失败')
        );
        return false;
      } catch (error) {
        console.error(editData ? '编辑版本失败:' : '添加版本失败:', error);
        messageApi.error(editData ? '编辑版本失败' : '添加版本失败');
        return false;
      }
    },
    formProps: {
      initialValues: editData || { versionUpdate: 0 },
    },
  });

  const translateContent = useCallback(
    async (content: string) => {
      if (!content) return;
      try {
        setTranslating(true);
        const [enResp, jaResp, zhResp] = await Promise.all([
          translate({ content, language: 'en' }),
          translate({ content, language: 'ja' }),
          translate({ content, language: 'zh' }),
        ]);

        if (
          enResp.status === 200 &&
          jaResp.status === 200 &&
          zhResp.status === 200
        ) {
          form.setFieldsValue({
            versionContentEn: enResp.data,
            versionContentJa: jaResp.data,
            versionContentZh: zhResp.data,
          });
        }
      } catch (error) {
        console.error('翻译失败:', error);
        messageApi.error('翻译失败');
      } finally {
        setTranslating(false);
      }
    },
    [form, messageApi]
  );

  const debouncedTranslate = useMemo(
    () => debounce((content: string) => translateContent(content), 2000),
    [translateContent]
  );

  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const content = e.target.value;
      form.setFieldValue('versionContent', content);

      if (autoTranslate && content) {
        debouncedTranslate(content);
      }
    },
    [form, autoTranslate, debouncedTranslate]
  );

  const handleManualTranslate = useCallback(() => {
    const content = form.getFieldValue('versionContent');
    if (!content) {
      messageApi.warning('请先输入版本内容');
      return;
    }
    if (!translating) {
      translateContent(content);
    }
  }, [form, translateContent, messageApi]);

  const drawer = useMemo(
    () => (
      <DrawerForm open={visible} className={styles.versionDrawer} >
        <div className={styles.formContainer}>
          <div className={styles.inputSection}>
            <Form.Item
              name="versionNumber"
              label="版本号"
              rules={[{ required: true, message: '请输入版本号' }]}
            >
              <Input placeholder="请输入版本号" />
            </Form.Item>
            <Form.Item
              name="versionContent"
              label={
                <Space>
                  版本内容
                  <Button
                    type="text"
                    size="small"
                    icon={<TranslationOutlined />}
                    className={styles.autoTranslateButton}
                    onClick={() => setAutoTranslate(!autoTranslate)}
                  >
                    {autoTranslate ? '关闭自动翻译' : '开启自动翻译'}
                  </Button>
                  {!autoTranslate && (
                    <TranslateButton
                      onTranslate={handleManualTranslate}
                      loading={translating}
                    />
                  )}
                </Space>
              }
              rules={[{ required: true, message: '请输入版本内容' }]}
            >
              <TextArea
                rows={12}
                placeholder="请输入版本内容"
                onChange={handleContentChange}
                showCount
              />
            </Form.Item>
            <Form.Item
              name="versionUpdate"
              label="是否强制更新"
              rules={[{ required: true, message: '请选择是否强制更新' }]}
            >
              <Radio.Group>
                <Radio value={0}>否</Radio>
                <Radio value={1}>是</Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          <Card
            className={styles.translationCard}
            title="多语言版本"
            extra={
              translating && (
                <LoadingOutlined style={{ color: token.colorPrimary }} />
              )
            }
            bordered={false}
          >
            <div className={styles.translationGrid}>
              <div className={styles.translationColumn}>
                <Form.Item
                  name="versionContentZh"
                  label="中文版本内容"
                  rules={[{ required: true, message: '请输入中文版本内容' }]}
                >
                  <TextArea rows={15} placeholder="中文版本内容" showCount />
                </Form.Item>
              </div>
              <div className={styles.translationColumn}>
                <Form.Item
                  name="versionContentEn"
                  label="英文版本内容"
                  rules={[{ required: true, message: '请输入英文版本内容' }]}
                >
                  <TextArea rows={15} placeholder="英文版本内容" showCount />
                </Form.Item>
              </div>
              <div className={styles.translationColumn}>
                <Form.Item
                  name="versionContentJa"
                  label="日文版本内容"
                  rules={[{ required: true, message: '请输入日文版本内容' }]}
                >
                  <TextArea rows={15} placeholder="日文版本内容" showCount />
                </Form.Item>
              </div>
            </div>
          </Card>
        </div>
      </DrawerForm>
    ),
    [visible, autoTranslate, handleContentChange, handleManualTranslate]
  );

  return (
    <>
      {contextHolder}
      {drawer}
    </>
  );
};

export default VersionDrawer;
